import type {
  AttentionItem,
  BudgetView,
  CashflowPoint,
  Counterparty,
  Direction,
  PaymentFact,
  Portfolio,
  ProjectDetail,
  ProjectStage,
  ProjectSummary,
} from '@epc/contracts';
import { queryOptions } from '@tanstack/react-query';
import type { Tone } from '../ui/feedback';
import { api } from './api';

export const portfolioQuery = (legalEntityId?: string) =>
  queryOptions({
    queryKey: ['portfolio', legalEntityId ?? 'all'],
    queryFn: () => api<Portfolio>('/portfolio', { query: { legalEntityId } }),
  });

export const projectsQuery = (status: 'active' | 'archived' | 'draft' | 'all' = 'active') =>
  queryOptions({
    queryKey: ['projects', status],
    queryFn: () => api<ProjectSummary[]>('/projects', { query: { status } }),
  });

export const projectQuery = (id: string) =>
  queryOptions({
    queryKey: ['project', id],
    queryFn: () => api<ProjectDetail>(`/projects/${id}`),
  });

export const budgetQuery = (id: string, versionId?: string) =>
  queryOptions({
    queryKey: ['budget', id, versionId ?? 'current'],
    queryFn: () => api<BudgetView>(`/projects/${id}/budget`, { query: { versionId } }),
  });

export const cashflowQuery = (id: string) =>
  queryOptions({
    queryKey: ['cashflow', id],
    queryFn: () => api<CashflowPoint[]>(`/projects/${id}/cashflow`),
  });

export const paymentsQuery = (id: string) =>
  queryOptions({
    queryKey: ['payments', id],
    queryFn: () => api<PaymentFact[]>(`/projects/${id}/payments`),
  });

export const attentionQuery = (projectId?: string) =>
  queryOptions({
    queryKey: ['attention', projectId ?? 'all'],
    queryFn: () => api<AttentionItem[]>('/attention', { query: { projectId } }),
  });

export const directionsQuery = queryOptions({
  queryKey: ['directions'],
  queryFn: () => api<Direction[]>('/directions'),
  staleTime: 5 * 60_000,
});

export const counterpartiesQuery = (kind?: 'customer' | 'supplier' | 'contractor' | 'designer') =>
  queryOptions({
    queryKey: ['counterparties', kind ?? 'all'],
    queryFn: () => api<Counterparty[]>('/counterparties', { query: { kind } }),
    staleTime: 60_000,
  });

export const STAGE_TONE: Record<ProjectStage, Tone> = {
  preparation: 'neutral',
  tender: 'info',
  design: 'neutral',
  expertise: 'neutral',
  construction: 'ink',
  commissioning: 'ink',
  warranty: 'success',
  closed: 'neutral',
};

export const SEVERITY_TONE: Record<AttentionItem['severity'], Tone> = {
  critical: 'danger',
  warning: 'warning',
  info: 'neutral',
};

export function projectTitle(p: { siteName: string | null; name: string }): string {
  return p.siteName ? `${p.siteName} · ${p.name}` : p.name;
}

/** Суммы в миллионах для таблиц: 3 184,1 */
export function mln(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(v / 1e6);
}
